import {
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  Mesh,
  ShapeUtils,
  Vector2,
  type MeshStandardMaterial,
} from "three";

import {
  isSuppressedAuthoredGenericStructureId,
  isSuppressedGelezinioVilkoGenericStructureSegment,
} from "../../config/buildingPassages.ts";
import {
  VILNIUS_CIVIC_SQUARE_REPLACED_HARDSCAPE_IDS,
} from "../../config/civicSquares.ts";
import type { StreetSurfaceFamily } from "../../config/streets.ts";
import type {
  RuntimeAreaFeature,
  RuntimeLinearFeature,
} from "../../world/schema/runtimeTile.ts";
import type { TerrainTileData, WorldTileId } from "../../world/schema/tile.ts";
import { sampleTerrainTileData } from "../../world/terrain.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";

export interface UrbanStructureBatch {
  readonly object: Group;
  readonly structureCount: number;
  readonly hardscapePolygonCount: number;
}

type HardscapeMaterialGroup =
  | "pedestrian"
  | "carriageway"
  | "unpaved"
  | "marking";

const SURFACE_LIFT_M = 0.035;
const ROAD_MARKING_LIFT_M = 0.07;
const STRUCTURE_BASE_SINK_M = 0.08;

export function createUrbanStructureBatch(
  tileId: WorldTileId,
  structures: readonly Readonly<RuntimeLinearFeature>[],
  hardscape: readonly Readonly<RuntimeAreaFeature>[],
  terrain: Readonly<TerrainTileData>,
  family: StreetSurfaceFamily,
  materials: CityMaterialLibrary,
): UrbanStructureBatch | null {
  if (structures.length === 0 && hardscape.length === 0) {
    return null;
  }
  const object = new Group();
  object.name = `runtime-urban-structures:${tileId}`;
  object.userData = {
    tileId,
    sourceLayer: "osm-urban-structures",
    representation:
      "source-positioned-terrain-following-hardscape-stairs-barriers-and-retaining-edges",
  };

  const hardscapeGroups: readonly {
    key: HardscapeMaterialGroup;
    material: MeshStandardMaterial;
  }[] = [
    {
      key: "pedestrian",
      material: materials.streetSurfaces[family].pedestrian,
    },
    {
      key: "carriageway",
      material: materials.streetSurfaces[family].carriageway,
    },
    { key: "unpaved", material: materials.terrain[0] },
    { key: "marking", material: materials.roadMarking },
  ];
  const renderableHardscape = hardscape.filter(
    (feature) =>
      !VILNIUS_CIVIC_SQUARE_REPLACED_HARDSCAPE_IDS.has(feature.id),
  );
  for (const group of hardscapeGroups) {
    addMesh(
      object,
      createHardscapeGeometry(
        renderableHardscape.filter((feature) =>
          hardscapeMaterialGroup(feature.classCode) === group.key
        ),
        terrain,
      ),
      group.material,
      `${object.name}:hardscape-${group.key}`,
      true,
    );
  }

  const renderableStructures = structures.filter(
    (feature) => !isSuppressedAuthoredGenericStructureId(feature.id),
  );
  const steps = renderableStructures.filter((feature) =>
    feature.classCode === "steps"
  );
  addMesh(
    object,
    createStepGeometry(steps, terrain),
    materials.streetSurfaces[family].sidewalk,
    `${object.name}:steps`,
    true,
  );

  const fences = renderableStructures.filter((feature) =>
    feature.classCode === "fence"
  );
  addMesh(
    object,
    createFenceGeometry(fences, terrain),
    materials.streetPole,
    `${object.name}:fences`,
    false,
  );

  const masonry = renderableStructures.filter((feature) =>
    feature.classCode !== "steps" && feature.classCode !== "fence"
  );
  addMesh(
    object,
    createBarrierGeometry(masonry, terrain),
    materials.streetSurfaces[family].curb,
    `${object.name}:walls-and-retaining-edges`,
    true,
  );

  if (object.children.length === 0) {
    return null;
  }
  return {
    object,
    structureCount: structures.length,
    hardscapePolygonCount: renderableHardscape.reduce(
      (count, feature) => count + feature.polygons.length,
      0,
    ),
  };
}

export function hardscapeMaterialGroup(
  classCode: string,
): HardscapeMaterialGroup {
  const normalized = classCode.toLowerCase();
  if (normalized.includes(":crossing-marking:")) {
    return "marking";
  }
  if (
    /:(?:unpaved|ground|dirt|earth|sand|gravel|fine_gravel|compacted|grass_paver)$/u
      .test(normalized)
  ) {
    return "unpaved";
  }
  if (
    normalized.includes(":parking:") ||
    normalized.includes(":service:") ||
    normalized.includes(":residential:") ||
    normalized.includes(":yes:")
  ) {
    return "carriageway";
  }
  return "pedestrian";
}

function createHardscapeGeometry(
  features: readonly Readonly<RuntimeAreaFeature>[],
  terrain: Readonly<TerrainTileData>,
): BufferGeometry | null {
  const positions: number[] = [];
  const indices: number[] = [];
  for (const feature of features) {
    const liftM = feature.classCode.toLowerCase().includes(
        ":crossing-marking:"
      )
      ? ROAD_MARKING_LIFT_M
      : SURFACE_LIFT_M;
    for (const polygon of feature.polygons) {
      const outer = stripClosingPoint(polygon.outer);
      const holes = polygon.holes.map(stripClosingPoint);
      if (outer.length < 3) {
        continue;
      }
      const faces = ShapeUtils.triangulateShape(
        outer.map((point) => new Vector2(point.x, point.z)),
        holes.map((hole) =>
          hole.map((point) => new Vector2(point.x, point.z))
        ),
      );
      const points = [...outer, ...holes.flat()];
      const base = positions.length / 3;
      for (const point of points) {
        positions.push(
          point.x,
          sampleHeight(terrain, point.x, point.z) + liftM,
          point.z,
        );
      }
      for (const face of faces) {
        const [first, second, third] = face;
        if (
          first !== undefined &&
          second !== undefined &&
          third !== undefined
        ) {
          const a = points[first]!;
          const b = points[second]!;
          const c = points[third]!;
          const signedAreaXz =
            (b.x - a.x) * (c.z - a.z) -
            (b.z - a.z) * (c.x - a.x);
          if (signedAreaXz > 0) {
            indices.push(base + first, base + third, base + second);
          } else {
            indices.push(base + first, base + second, base + third);
          }
        }
      }
    }
  }
  return geometryFrom(positions, indices);
}

function createStepGeometry(
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
): BufferGeometry | null {
  const positions: number[] = [];
  const indices: number[] = [];
  for (const feature of features) {
    const widthM = clamp(feature.widthM ?? 2.2, 1, 6);
    forEachDensifiedSegment(feature, 0.8, (from, to) => {
      const dx = to.x - from.x;
      const dz = to.z - from.z;
      const lengthM = Math.hypot(dx, dz);
      if (lengthM < 0.02) {
        return;
      }
      const nx = -dz / lengthM * widthM / 2;
      const nz = dx / lengthM * widthM / 2;
      const fromY = sampleHeight(terrain, from.x, from.z) + SURFACE_LIFT_M;
      const toY = sampleHeight(terrain, to.x, to.z) + SURFACE_LIFT_M;
      const base = positions.length / 3;
      positions.push(
        from.x + nx, fromY, from.z + nz,
        from.x - nx, fromY, from.z - nz,
        to.x + nx, toY, to.z + nz,
        to.x - nx, toY, to.z - nz,
      );
      indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
    });
  }
  return geometryFrom(positions, indices);
}

function createBarrierGeometry(
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
): BufferGeometry | null {
  const positions: number[] = [];
  const indices: number[] = [];
  for (const feature of features) {
    const defaults = barrierDimensions(feature.classCode);
    const widthM = clamp(feature.widthM ?? defaults.widthM, 0.08, 2.5);
    const heightM = clamp(feature.heightM ?? defaults.heightM, 0.25, 12);
    forEachDensifiedSegment(feature, 4, (from, to) => {
      appendSegmentPrism(
        positions,
        indices,
        from,
        to,
        sampleHeight(terrain, from.x, from.z) - STRUCTURE_BASE_SINK_M,
        sampleHeight(terrain, to.x, to.z) - STRUCTURE_BASE_SINK_M,
        widthM,
        heightM,
      );
    });
  }
  return geometryFrom(positions, indices);
}

function createFenceGeometry(
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
): BufferGeometry | null {
  const positions: number[] = [];
  const indices: number[] = [];
  for (const feature of features) {
    const heightM = clamp(feature.heightM ?? 1.35, 0.5, 4);
    const railWidthM = clamp(feature.widthM ?? 0.07, 0.04, 0.2);
    for (let index = 1; index < feature.points.length; index += 1) {
      if (
        isSuppressedGelezinioVilkoGenericStructureSegment(
          feature.id,
          index - 1,
        )
      ) {
        continue;
      }
      const from = feature.points[index - 1];
      const to = feature.points[index];
      if (from === undefined || to === undefined) {
        continue;
      }
      const lengthM = Math.hypot(to.x - from.x, to.z - from.z);
      if (lengthM < 0.02) {
        continue;
      }
      const sections = Math.max(1, Math.ceil(lengthM / 3));
      for (let section = 0; section <= sections; section += 1) {
        const factor = section / sections;
        const x = from.x + (to.x - from.x) * factor;
        const z = from.z + (to.z - from.z) * factor;
        appendAxisBox(
          positions,
          indices,
          x,
          sampleHeight(terrain, x, z) - STRUCTURE_BASE_SINK_M,
          z,
          Math.max(0.09, railWidthM * 1.6),
          heightM,
        );
      }
      const fromGround = sampleHeight(terrain, from.x, from.z);
      const toGround = sampleHeight(terrain, to.x, to.z);
      for (const railFactor of [0.35, 0.82]) {
        appendSegmentPrism(
          positions,
          indices,
          from,
          to,
          fromGround + heightM * railFactor,
          toGround + heightM * railFactor,
          railWidthM,
          Math.max(0.06, railWidthM),
        );
      }
    }
  }
  return geometryFrom(positions, indices);
}

function appendSegmentPrism(
  positions: number[],
  indices: number[],
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
  fromBaseY: number,
  toBaseY: number,
  widthM: number,
  heightM: number,
): void {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const lengthM = Math.hypot(dx, dz);
  if (lengthM < 0.02) {
    return;
  }
  const nx = -dz / lengthM * widthM / 2;
  const nz = dx / lengthM * widthM / 2;
  const base = positions.length / 3;
  positions.push(
    from.x + nx, fromBaseY, from.z + nz,
    from.x - nx, fromBaseY, from.z - nz,
    to.x + nx, toBaseY, to.z + nz,
    to.x - nx, toBaseY, to.z - nz,
    from.x + nx, fromBaseY + heightM, from.z + nz,
    from.x - nx, fromBaseY + heightM, from.z - nz,
    to.x + nx, toBaseY + heightM, to.z + nz,
    to.x - nx, toBaseY + heightM, to.z - nz,
  );
  indices.push(
    base, base + 2, base + 4, base + 2, base + 6, base + 4,
    base + 1, base + 5, base + 3, base + 3, base + 5, base + 7,
    base + 4, base + 6, base + 5, base + 5, base + 6, base + 7,
    base, base + 4, base + 1, base + 1, base + 4, base + 5,
    base + 2, base + 3, base + 6, base + 3, base + 7, base + 6,
  );
}

function appendAxisBox(
  positions: number[],
  indices: number[],
  x: number,
  baseY: number,
  z: number,
  widthM: number,
  heightM: number,
): void {
  const half = widthM / 2;
  const base = positions.length / 3;
  positions.push(
    x - half, baseY, z - half,
    x + half, baseY, z - half,
    x - half, baseY, z + half,
    x + half, baseY, z + half,
    x - half, baseY + heightM, z - half,
    x + half, baseY + heightM, z - half,
    x - half, baseY + heightM, z + half,
    x + half, baseY + heightM, z + half,
  );
  indices.push(
    base, base + 4, base + 1, base + 1, base + 4, base + 5,
    base + 2, base + 3, base + 6, base + 3, base + 7, base + 6,
    base, base + 2, base + 4, base + 2, base + 6, base + 4,
    base + 1, base + 5, base + 3, base + 3, base + 5, base + 7,
    base + 4, base + 6, base + 5, base + 5, base + 6, base + 7,
  );
}

function forEachDensifiedSegment(
  feature: Readonly<RuntimeLinearFeature>,
  maximumLengthM: number,
  callback: (
    from: Readonly<{ x: number; z: number }>,
    to: Readonly<{ x: number; z: number }>,
  ) => void,
): void {
  for (let index = 1; index < feature.points.length; index += 1) {
    const from = feature.points[index - 1];
    const to = feature.points[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const lengthM = Math.hypot(to.x - from.x, to.z - from.z);
    const sections = Math.max(1, Math.ceil(lengthM / maximumLengthM));
    for (let section = 0; section < sections; section += 1) {
      const fromFactor = section / sections;
      const toFactor = (section + 1) / sections;
      callback(
        {
          x: from.x + (to.x - from.x) * fromFactor,
          z: from.z + (to.z - from.z) * fromFactor,
        },
        {
          x: from.x + (to.x - from.x) * toFactor,
          z: from.z + (to.z - from.z) * toFactor,
        },
      );
    }
  }
}

function barrierDimensions(classCode: string): Readonly<{
  widthM: number;
  heightM: number;
}> {
  switch (classCode) {
    case "retaining_wall":
      return { widthM: 0.45, heightM: 1.5 };
    case "cliff":
      return { widthM: 0.55, heightM: 2.2 };
    case "embankment":
      return { widthM: 0.5, heightM: 0.9 };
    default:
      return { widthM: 0.3, heightM: 1.8 };
  }
}

function sampleHeight(
  terrain: Readonly<TerrainTileData>,
  x: number,
  z: number,
): number {
  const maximum = terrain.spacingM * (terrain.samplesPerSide - 1);
  return sampleTerrainTileData(
    terrain,
    { x: 0, z: 0 },
    {
      x: clamp(x, 0, maximum),
      z: clamp(z, 0, maximum),
    },
  ) ?? 0;
}

function stripClosingPoint<T extends Readonly<{ x: number; z: number }>>(
  ring: readonly T[],
): readonly T[] {
  const first = ring[0];
  const last = ring[ring.length - 1];
  return first !== undefined && last !== undefined &&
      first.x === last.x && first.z === last.z
    ? ring.slice(0, -1)
    : ring;
}

function geometryFrom(
  positions: readonly number[],
  indices: readonly number[],
): BufferGeometry | null {
  if (positions.length === 0 || indices.length === 0) {
    return null;
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(positions, 3),
  );
  geometry.setIndex([...indices]);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function addMesh(
  group: Group,
  geometry: BufferGeometry | null,
  material: MeshStandardMaterial,
  name: string,
  receiveShadow: boolean,
): void {
  if (geometry === null) {
    return;
  }
  const mesh = new Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = !receiveShadow;
  mesh.receiveShadow = receiveShadow;
  group.add(mesh);
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
